import {Subscription} from 'rxjs';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  forwardRef,
  HostBinding,
  OnDestroy,
  OnInit,
  Renderer2,
  ViewChild,
} from '@angular/core';
import {ControlValueAccessor, FormControl, FormsModule, NG_VALUE_ACCESSOR, ReactiveFormsModule} from '@angular/forms';
import {DecimalPipe, NgClass, NgOptimizedImage} from '@angular/common';
import {MatSlider, MatSliderThumb} from '@angular/material/slider';

@Component({
  selector: 'app-dashboard-study-viewer-zoomed-controls-zoom',
  templateUrl: './dashboard-study-viewer-zoomed-controls-zoom.component.html',
  styleUrls: ['./dashboard-study-viewer-zoomed-controls-zoom.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DashboardStudyViewerZoomedControlsZoomComponent),
      multi: true,
    },
  ],
  imports: [NgClass, NgOptimizedImage, MatSlider, MatSliderThumb, FormsModule, ReactiveFormsModule, DecimalPipe],
})
export class DashboardStudyViewerZoomedControlsZoomComponent implements OnInit, ControlValueAccessor, OnDestroy {
  @ViewChild('slider') public slider?: ElementRef<HTMLInputElement>;

  /** Triggered whether the value is changed. Will be overwritten by Angular forms. */
  public onChange?: (value: number) => void;

  /** Triggered whether the control is touched. Will be overwritten by Angular forms. */
  public onTouched?: () => void;

  /** Contain the minimal zoom available. */
  public readonly minimalZoomAvailable = 1;

  /** Contain the maximal zoom available. */
  public readonly maximalZoomAvailable = 10;

  /** Contain the zoom step. */
  public readonly zoomStep = 0.1;

  /** Contain the default zoom. */
  public readonly defaultZoom = 1;

  /** Contain the zoom control with the currently applied zoom. */
  public zoomControl = new FormControl<number>(1);

  @HostBinding('class.disabled') protected isDisabled = false;

  /** Component subscriptions. Will be unsubscribed on component destroy. */
  private readonly subscription = new Subscription();

  constructor(protected renderer: Renderer2, protected cd: ChangeDetectorRef) {}

  ngOnInit(): void {
    // Emit value to the parent component.
    this.subscription.add(
      this.zoomControl.valueChanges.subscribe({
        next: value => {
          if (this.onChange) {
            this.onChange(value);
          }
        },
      })
    );
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  /**
   * Required by ControlValueAccessor.
   * Function for overriding the `onChange` class method.
   */
  public registerOnChange(fn: (value: any) => void): void {
    // Override the class method with provided function.
    this.onChange = fn;
  }

  /**
   * Required by ControlValueAccessor.
   * Function for overriding the `onTouched` class method.
   */
  public registerOnTouched(fn: () => void): void {
    // Override the class method with provided function.
    this.onTouched = fn;
  }

  /**
   * Required by ControlValueAccessor.
   * Describes how to update the form model when the value changes.
   */
  public writeValue(value: number): void {
    this.safeUpdateControlValue(value);
  }

  /**
   * Required by ControlValueAccessor.
   * Describes what actions should be taken when the control status changes.
   */
  public setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
    if (this.slider) {
      this.renderer.setProperty(this.slider.nativeElement, 'disabled', isDisabled);
    }
  }

  /**
   * Update the zoom value by increasing it for the zoom step.
   */
  public zoomOut(): void {
    this.safeUpdateControlValue(this.zoomControl.value - this.zoomStep);
  }

  /**
   * Update the zoom value by decreasing it for the zoom step.
   */
  public zoomIn(): void {
    this.safeUpdateControlValue(this.zoomControl.value + this.zoomStep);
  }

  /**
   * Update the zoom value using the provided value.
   */
  public safeUpdateControlValue(value: number) {
    const safeValue = typeof value === 'number' ? value : this.defaultZoom;
    const roundValue = +safeValue.toFixed(1);

    if (roundValue < this.minimalZoomAvailable) {
      this.zoomControl.setValue(this.minimalZoomAvailable);
    } else if (roundValue > this.maximalZoomAvailable) {
      this.zoomControl.setValue(this.maximalZoomAvailable);
    } else {
      this.zoomControl.setValue(roundValue);
    }
  }
}
